import { Box, Card, CardContent, Stack, ToggleButton, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import { isEmpty, isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import NoDataAlert from "@/common/components/AppAlerts/NoDataAlert";
import AppIconButton from "@/common/components/Button/AppIconButton";
import Datetime from "@/common/components/Datetime/Datetime";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import FieldValue from "@/common/components/Form/Display/FieldValue";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import AppIcon from "@/common/components/Icons/AppIcon";
import HelpTooltipIcon from "@/common/components/Icons/HelpTooltipIcon";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditMileageModal from "@/common/components/Modals/common/BaseEditMileageModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { cleanlinessTags, damageTags } from "@/common/constants/vehicleInsightsTags";
import { FileItem } from "@/common/fileItem";
import { enumService } from "@/common/services/enum";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import { AppPermission, EntityType, VisualInspectionDto } from "@/core/api/generated/v1";

import ContractLink from "../../../Contract/ContractLink";
import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import GeneralInspectorDisplay from "../../../General/Display/GeneralInspectorDisplay";
import GeneralDeferredValueDisplay from "../../../GeneralDeferredValue/GeneralDeferredValueDisplay";
import VehicleCleanlinessExtentInline from "../../../Vehicle/VehicleCleanlinessExtentInline";
import VehicleDamageExtentInline from "../../../Vehicle/VehicleDamageExtentInline";
import VehicleLink from "../../../Vehicle/VehicleLink";
import VisualInspectionSettingsDisplay from "../../VisualInspectionSettingsDisplay";

// Styled ToggleButton for filter controls
const StyledToggleButton = styled(ToggleButton)(({ theme }) => ({
  borderRadius: theme.shapeCustom.borderRadiusSquareAppIconButton,
  textTransform: "lowercase",
  fontWeight: 500,
  paddingLeft: theme.spacing(1.5),
  paddingRight: theme.spacing(1.5),
  paddingTop: 0,
  paddingBottom: 0,
  fontSize: theme.typography.pxToRem(12),
  border: "none",
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.gray,
  "&.Mui-selected": {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    "&:hover": {
      backgroundColor: theme.palette.primary.main,
    },
  },
  "&.Mui-disabled": {
    color: "#bdbdbd",
    backgroundColor: "#f5f5f5",
  },
}));

interface Props {
  visualInspection: VisualInspectionDto;
  onUpdate: (newValue: Nil<VisualInspectionDto>) => void;
}

export default function OverviewTabContent({ visualInspection, onUpdate }: Props) {
  const [isUpdateMileageModalOpened, setIsUpdateMileageModalOpened] = useState(false);
  // Multi-select filter state per item
  const [selectedFilters, setSelectedFilters] = useState<Record<string, Array<"all" | string>>>({});

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={visualInspection} />}
          />
        }
      >
        <ViewContentBlock>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            {/* <InfoTile
              label='Number'
              iconOf='localNumber'
              isEmpty={isNil(visualInspection?.localNumber)}
            >
              {visualInspection?.localNumber}
            </InfoTile> */}

            <InfoTile label='Vehicle' iconOf='vehicle' isEmpty={isNil(visualInspection?.vehicle)}>
              <VehicleLink entity={visualInspection?.vehicle} />
            </InfoTile>

            <InfoTile
              label='Contract'
              iconOf='contract'
              isEmpty={isNil(visualInspection?.contract)}
            >
              <ContractLink entity={visualInspection?.contract} />
            </InfoTile>

            <InfoTile
              label='External number'
              iconOf='externalNumber'
              isEmpty={!visualInspection.externalNumber}
              endAdornment={
                <BaseEditExternalNumberModal
                  initialValues={{ externalNumber: visualInspection.externalNumber }}
                  onConfirm={async (externalNumber) => {
                    const response =
                      await apiClient.visualInspectionsApi.apiV1VisualInspectionsVisualInspectionIdExternalNumberPut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          visualInspectionId: visualInspection.id || "",
                          generalExternalNumberInputDto: { externalNumber },
                        },
                      );
                    onUpdate(response.data);
                  }}
                />
              }
            >
              {visualInspection.externalNumber}
            </InfoTile>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile
              label='Inspected at'
              iconOf='date'
              isEmpty={isNil(visualInspection?.inspectedAt)}
            >
              <Datetime datetime={visualInspection?.inspectedAt} />
            </InfoTile>

            <InfoTile
              label='Inspector'
              iconOf='inspector'
              isEmpty={isNil(visualInspection?.inspector)}
            >
              <GeneralInspectorDisplay
                inspector={visualInspection?.inspector}
                inlineUserProps={{ withLink: true, withAvatar: true, withPoper: true }}
              />
            </InfoTile>

            <InfoTile
              label='Notes'
              iconOf='notes'
              isEmpty={!visualInspection.notes}
              endAdornment={
                <BaseEditNotesModal
                  initialValues={{ notes: visualInspection.notes }}
                  onConfirm={async (notes) => {
                    const response =
                      await apiClient.visualInspectionsApi.apiV1VisualInspectionsVisualInspectionIdNotesPut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          visualInspectionId: visualInspection.id || "",
                          generalNotesInputDto: { notes },
                        },
                      );
                    onUpdate(response.data);
                  }}
                />
              }
            >
              {visualInspection.notes}
            </InfoTile>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile
              label='Mileage'
              iconOf='gauge'
              isEmpty={!visualInspection.mileage}
              endAdornment={
                <BaseEditMileageModal
                  vehicleId={visualInspection.vehicle?.id}
                  mileage={visualInspection.mileage}
                  trigger={
                    <AppIconButton
                      sx={{ p: "2px", ml: 0.5 }}
                      onClick={() => setIsUpdateMileageModalOpened(true)}
                    >
                      <AppIcon of='edit' sx={{ width: "0.8em", height: "0.8em" }} />
                    </AppIconButton>
                  }
                  triggerParams={{ isDisableDefaultAction: true }}
                  open={isUpdateMileageModalOpened}
                  onClose={() => setIsUpdateMileageModalOpened(false)}
                  onConfirm={async (mileage) => {
                    await apiClient.visualInspectionsApi.apiV1VisualInspectionsVisualInspectionIdMileagePut(
                      {
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        visualInspectionId: visualInspection.id || "",
                        updateVehicleMileageDto: {
                          vehicleId: visualInspection.vehicle?.id,
                          mileage: mileage,
                        },
                      },
                    );
                  }}
                  onMileageChanged={() => onUpdate(null)}
                />
              }
            >
              {visualInspection.mileage?.mileage} {visualInspection.mileage?.unit}
            </InfoTile>

            <InfoTile label='Fuel level' iconOf='fuel' isEmpty={isNil(visualInspection?.fuelLevel)}>
              {visualInspection?.fuelLevel}
            </InfoTile>
          </Box>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.VisualInspection}
          entityId={visualInspection.id}
          entity={visualInspection}
          entityUpdatePermission={AppPermission.VisualInspectionManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={() => onUpdate(null)}
        />

        {!isEmpty(visualInspection?.attachments) && (
          <ViewContentBlock>
            <FileListView
              files={FileItem.createManyFrom(visualInspection?.attachments)}
              maxVisibleFiles={10}
              itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
              itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            />
          </ViewContentBlock>
        )}

        {/* Inspections */}
        <ViewContentBlock>
          <Typography component='span' variant='h3'>
            Inspections
          </Typography>

          {isEmpty(visualInspection?.items) && <NoDataAlert />}

          <Stack direction='column' spacing={1}>
            {visualInspection?.items?.map((item, index) => {
              const attachments = item?.attachments || [];
              const fileItems = FileItem.createManyFrom(attachments);

              // Find which tags are present in this item's attachments
              const presentCleanliness = cleanlinessTags.filter((tag) =>
                fileItems.some(
                  (fileItem) =>
                    fileItem?.attachment?.vehicleInsights?.value?.vehicleCleanlinessExtent === tag,
                ),
              );
              const presentDamage = damageTags.filter((tag) =>
                fileItems.some(
                  (fileItem) =>
                    fileItem?.attachment?.vehicleInsights?.value?.vehicleDamageExtent === tag,
                ),
              );

              // Filtering logic
              const itemId = item.id || String(index);
              const itemFilters = selectedFilters[itemId] || ["all"];
              const showAll = itemFilters.includes("all");
              const filteredFileItems = showAll
                ? fileItems
                : fileItems.filter((fileItem) => {
                    const vi = fileItem?.attachment?.vehicleInsights?.value;
                    const cleanliness = vi?.vehicleCleanlinessExtent;
                    const damage = vi?.vehicleDamageExtent;
                    return (
                      (cleanliness && itemFilters.includes(cleanliness)) ||
                      (damage && itemFilters.includes(damage))
                    );
                  });

              // Handle filter change per item
              const handleFilterChange = (
                event: React.MouseEvent<HTMLElement>,
                newFilters: Array<"all" | string>,
              ) => {
                setSelectedFilters((prev) => {
                  let updated: Array<"all" | string>;
                  if (newFilters.includes("all")) {
                    updated = ["all"];
                  } else if (newFilters.length === 0) {
                    updated = ["all"];
                  } else {
                    updated = newFilters.filter((f) => f !== "all");
                  }
                  return { ...prev, [itemId]: updated };
                });
              };

              return (
                <Card key={index}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack flexDirection={"row"} alignItems='center' gap={1}>
                        <Typography component='span' variant='subtitle1' sx={{ mr: 2 }}>
                          <InlineApiEnumValue type='VehicleArea' value={item.area} />
                        </Typography>

                        {/* Filter buttons */}
                        <Box
                          sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1,
                            background: "transparent",
                            boxShadow: "none",
                          }}
                        >
                          <StyledToggleButton
                            value='all'
                            aria-label='all images'
                            selected={showAll}
                            onClick={(event) => handleFilterChange(event, ["all"])}
                            sx={{ color: (theme) => theme.palette.text.main }}
                          >
                            all images
                          </StyledToggleButton>
                          {damageTags.map((tag) => (
                            <StyledToggleButton
                              key={tag}
                              value={tag}
                              aria-label={tag}
                              selected={itemFilters.includes(tag)}
                              disabled={!presentDamage.includes(tag)}
                              onClick={(event) => {
                                let newFilters;
                                if (itemFilters.includes(tag)) {
                                  newFilters = itemFilters.filter((f) => f !== tag);
                                } else {
                                  newFilters = itemFilters.filter((f) => f !== "all").concat(tag);
                                }
                                if (newFilters.length === 0) newFilters = ["all"];
                                handleFilterChange(event, newFilters);
                              }}
                            >
                              {enumService.getEnumValueName("VehicleDamageExtent", tag)}
                            </StyledToggleButton>
                          ))}
                          {cleanlinessTags.map((tag) => (
                            <StyledToggleButton
                              key={tag}
                              value={tag}
                              aria-label={tag}
                              selected={itemFilters.includes(tag)}
                              disabled={!presentCleanliness.includes(tag)}
                              onClick={(event) => {
                                let newFilters;
                                if (itemFilters.includes(tag)) {
                                  newFilters = itemFilters.filter((f) => f !== tag);
                                } else {
                                  newFilters = itemFilters.filter((f) => f !== "all").concat(tag);
                                }
                                if (newFilters.length === 0) newFilters = ["all"];
                                handleFilterChange(event, newFilters);
                              }}
                            >
                              {enumService.getEnumValueName("VehicleCleanlinessExtent", tag)}
                            </StyledToggleButton>
                          ))}
                        </Box>
                      </Stack>
                      <Box>
                        <FileListView
                          files={filteredFileItems}
                          maxVisibleFiles={10}
                          itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
                          itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
                        />
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </ViewContentBlock>

        {/* Vehicle insights */}
        {visualInspection.vehicleInsights && (
          <ViewContentBlock>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Typography component='span' variant='h3'>
                Vehicle insights
              </Typography>
              <HelpTooltipIcon title='Vehicle insights generated by AI.' />
            </Stack>

            <GeneralDeferredValueDisplay
              value={visualInspection.vehicleInsights}
              pendingTitle='Vehicle insights are being generated, please wait.'
              noValue={<NoDataAlert title='No insights.' />}
            >
              <Stack spacing={1}>
                <FieldValue label='Damage severity'>
                  <VehicleDamageExtentInline
                    value={visualInspection.vehicleInsights?.value?.damageExtent}
                  />
                </FieldValue>

                <FieldValue label='Cleanliness'>
                  <VehicleCleanlinessExtentInline
                    value={visualInspection.vehicleInsights?.value?.cleanlinessExtent}
                  />
                </FieldValue>
              </Stack>
            </GeneralDeferredValueDisplay>
          </ViewContentBlock>
        )}

        <ViewContentBlock>
          {!visualInspection?.settings && <NoDataAlert />}
          <VisualInspectionSettingsDisplay settings={visualInspection?.settings} />
        </ViewContentBlock>
      </ViewLayout>
    </PageTabContent>
  );
}
